import type {
  DefenseAssignment,
  DefensePlayDef,
  DefenseRole,
  DefenseSlot,
  OffenseAssignment,
  OffensePlayDef,
  OffenseRole,
  OffenseSlot,
  PlayDef,
  Position,
  PlayRisk,
  Spot,
} from './types';

export const OFFENSE_SLOTS: readonly OffenseSlot[] = ['QB', 'RB', 'WR1', 'WR2', 'TE', 'OL1', 'OL2'];
export const DEFENSE_SLOTS: readonly DefenseSlot[] = [
  'DL1',
  'DL2',
  'LB1',
  'LB2',
  'CB1',
  'CB2',
  'S',
];

/** The position each slot plays, for position fit. */
export const SLOT_POSITION: Readonly<Record<OffenseSlot | DefenseSlot, Position>> = {
  QB: 'QB',
  RB: 'RB',
  WR1: 'WR',
  WR2: 'WR',
  TE: 'TE',
  OL1: 'OL',
  OL2: 'OL',
  DL1: 'DL',
  DL2: 'DL',
  LB1: 'LB',
  LB2: 'LB',
  CB1: 'CB',
  CB2: 'CB',
  S: 'S',
};

const OFFENSE_ROLES: readonly OffenseRole[] = ['pass', 'hand-off', 'block', 'route', 'carry'];
const DEFENSE_ROLES: readonly DefenseRole[] = ['rush', 'blitz', 'zone', 'man'];
const RISKS: readonly PlayRisk[] = ['low', 'medium', 'high'];

export class PlayDefError extends Error {
  override name = 'PlayDefError';
}

type Json = Record<string, unknown>;

function obj(v: unknown, where: string): Json {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) {
    throw new PlayDefError(`${where}: expected an object`);
  }
  return v as Json;
}

function str(v: unknown, where: string): string {
  if (typeof v !== 'string' || v.length === 0)
    throw new PlayDefError(`${where}: expected a string`);
  return v;
}

function strings(v: unknown, where: string): string[] {
  if (!Array.isArray(v)) throw new PlayDefError(`${where}: expected an array`);
  return v.map((x, i) => str(x, `${where}[${i}]`));
}

function slotRecord<S extends string, T>(
  v: unknown,
  slots: readonly S[],
  where: string,
  each: (x: unknown, at: string) => T,
): Record<S, T> {
  const o = obj(v, where);
  for (const k of Object.keys(o)) {
    if (!slots.includes(k as S)) throw new PlayDefError(`${where}: unknown slot "${k}"`);
  }
  const out = {} as Record<S, T>;
  for (const s of slots) {
    if (!(s in o)) throw new PlayDefError(`${where}: missing slot "${s}"`);
    out[s] = each(o[s], `${where}.${s}`);
  }
  return out;
}

function spot(v: unknown, where: string): Spot {
  if (
    !Array.isArray(v) ||
    v.length !== 2 ||
    !v.every((x) => typeof x === 'number' && Number.isFinite(x))
  ) {
    throw new PlayDefError(`${where}: expected a spot [downfield, lateral] in yards`);
  }
  return [v[0], v[1]];
}

function spots(v: unknown, where: string): Spot[] {
  if (!Array.isArray(v) || v.length === 0) {
    throw new PlayDefError(`${where}: expected a non-empty array of spots`);
  }
  return v.map((x, i) => spot(x, `${where}[${i}]`));
}

function moveId(v: unknown, where: string): string | null {
  return v === null ? null : str(v, where);
}

function offenseAssignment(v: unknown, where: string): OffenseAssignment {
  const o = obj(v, where);
  const role = str(o.role, `${where}.role`) as OffenseRole;
  if (!OFFENSE_ROLES.includes(role)) throw new PlayDefError(`${where}: unknown role "${role}"`);
  const align = spot(o.align, `${where}.align`);
  if (o.target !== undefined) {
    if (role !== 'block') throw new PlayDefError(`${where}: only a blocker has a target`);
    const target = str(o.target, `${where}.target`) as DefenseSlot;
    if (!DEFENSE_SLOTS.includes(target)) {
      throw new PlayDefError(`${where}: unknown target "${target}"`);
    }
    if (o.path !== undefined) {
      throw new PlayDefError(`${where}: only a route, a passer, or a carrier has a path`);
    }
    return { role, align, target };
  }
  if (role === 'route') {
    return {
      role,
      route: str(o.route, `${where}.route`),
      align,
      path: spots(o.path, `${where}.path`),
    };
  }
  if (role === 'carry') {
    const path = spots(o.path, `${where}.path`);
    if (path.at(-1)?.[0] !== 0) {
      throw new PlayDefError(
        `${where}: a carry path ends on the line (depth 0) at the designed gap`,
      );
    }
    return { role, align, path };
  }
  if (o.path !== undefined) {
    if (role !== 'pass') {
      throw new PlayDefError(`${where}: only a route, a passer, or a carrier has a path`);
    }
    return { role, align, path: spots(o.path, `${where}.path`) };
  }
  return { role, align };
}

function defenseAssignment(v: unknown, where: string): DefenseAssignment {
  const o = obj(v, where);
  const role = str(o.role, `${where}.role`) as DefenseRole;
  if (!DEFENSE_ROLES.includes(role)) throw new PlayDefError(`${where}: unknown role "${role}"`);
  const out: DefenseAssignment = { role, align: spot(o.align, `${where}.align`) };
  if (o.zone !== undefined) out.zone = str(o.zone, `${where}.zone`);
  if (o.drop !== undefined) out.drop = spot(o.drop, `${where}.drop`);
  if (o.target !== undefined) {
    const target = str(o.target, `${where}.target`) as OffenseSlot;
    if (!OFFENSE_SLOTS.includes(target)) {
      throw new PlayDefError(`${where}: unknown target "${target}"`);
    }
    out.target = target;
  }
  return out;
}

/** A play's blurb fits two or three lines of a phone tile (slice 8c). A format limit, not balance. */
export const BLURB_MAX_CHARS = 110;

function blurb(v: unknown, id: string): string {
  if (typeof v !== 'string' || v.trim().length === 0) {
    throw new PlayDefError(`${id}.blurb: a play needs a blurb, one plain-English line`);
  }
  if (v.length > BLURB_MAX_CHARS) {
    throw new PlayDefError(`${id}.blurb: at most ${BLURB_MAX_CHARS} characters`);
  }
  return v;
}

/** Validate a play JSON from packages/data/plays and return it typed. Throws PlayDefError. */
export function parsePlay(json: unknown): PlayDef {
  const o = obj(json, 'play');
  const id = str(o.id, 'play.id');
  const base = {
    id,
    name: str(o.name, `${id}.name`),
    blurb: blurb(o.blurb, id),
    formation: str(o.formation, `${id}.formation`),
    tags: strings(o.tags, `${id}.tags`),
    situations: strings(o.situations, `${id}.situations`),
    leansOn: strings(o.leansOn, `${id}.leansOn`),
    risk: str(o.risk, `${id}.risk`) as PlayRisk,
  };
  if (!RISKS.includes(base.risk)) {
    throw new PlayDefError(`${id}.risk: expected one of ${RISKS.join(', ')}`);
  }
  if (o.side === 'offense') {
    const assignments = slotRecord(
      o.assignments,
      OFFENSE_SLOTS,
      `${id}.assignments`,
      offenseAssignment,
    );
    const roles = OFFENSE_SLOTS.map((s) => assignments[s].role);
    const count = (r: OffenseRole) => roles.filter((x) => x === r).length;
    if (count('pass') + count('hand-off') !== 1) {
      throw new PlayDefError(`${id}: a play has one passer or one hand-off, not both`);
    }
    const targets = OFFENSE_SLOTS.flatMap((s) => assignments[s].target ?? []);
    const twice = targets.find((d, i) => targets.indexOf(d) !== i);
    if (twice !== undefined) {
      throw new PlayDefError(`${id}.assignments: two blockers target "${twice}"`);
    }
    const run = count('hand-off') === 1;
    if (run && count('carry') !== 1) {
      throw new PlayDefError(`${id}: a hand-off needs exactly one carrier`);
    }
    if (!run && count('carry') > 0) throw new PlayDefError(`${id}: a carrier needs a hand-off`);
    if (run && count('route') > 0) throw new PlayDefError(`${id}: a run has no routes`);
    const reads = strings(o.reads, `${id}.reads`) as OffenseSlot[];
    if (run && reads.length > 0) throw new PlayDefError(`${id}.reads: a run has no reads`);
    for (const r of reads) {
      if (assignments[r]?.role !== 'route') {
        throw new PlayDefError(`${id}.reads: "${r}" is not running a route`);
      }
    }
    if (o.playAction !== undefined && typeof o.playAction !== 'boolean') {
      throw new PlayDefError(`${id}.playAction: expected true or false`);
    }
    if (o.playAction === true && run) {
      throw new PlayDefError(`${id}.playAction: play-action is a pass`);
    }
    const play: OffensePlayDef = {
      ...base,
      side: 'offense',
      assignments,
      defaultMoves: slotRecord(o.defaultMoves, OFFENSE_SLOTS, `${id}.defaultMoves`, moveId),
      reads,
      ...(o.playAction === true ? { playAction: true } : {}),
    };
    return play;
  }
  if (o.side === 'defense') {
    const play: DefensePlayDef = {
      ...base,
      side: 'defense',
      assignments: slotRecord(o.assignments, DEFENSE_SLOTS, `${id}.assignments`, defenseAssignment),
      defaultMoves: slotRecord(o.defaultMoves, DEFENSE_SLOTS, `${id}.defaultMoves`, moveId),
    };
    return play;
  }
  throw new PlayDefError(`${id}.side: expected "offense" or "defense"`);
}

export function parseOffensePlay(json: unknown): OffensePlayDef {
  const p = parsePlay(json);
  if (p.side !== 'offense') throw new PlayDefError(`${p.id}: expected an offense play`);
  return p;
}

export function parseDefensePlay(json: unknown): DefensePlayDef {
  const p = parsePlay(json);
  if (p.side !== 'defense') throw new PlayDefError(`${p.id}: expected a defense play`);
  return p;
}
